import { type FC } from "react";
import { Table } from "@root/shared/components/Table/Table";
import { Td } from "@root/shared/components/Table/Td";
import { Th } from "@root/shared/components/Table/Th";
import { Tr } from "@root/shared/components/Table/Tr";
import { Button } from "@root/shared/components/Button";
import { RequestConfirmationPanel } from "@userFrontend/features/reward/components/RequestConfirmationPanel";
import { MultiLineText } from "@root/shared/components/MultiLineText";
import { ConfirmSalariesDataContents } from "@userFrontend/features/clientReview/components/ClientReviewInputs/uiParts/Confirm/uiParts/ConfirmSalariesDataContents";
import { EMPLOYMENT } from "@root/constants/employments";
import { MODEL_ATTRIBUTE_NAME } from "@root/shared/utils/constants";
import * as styles from "./styles.css";
// types
import { type Props } from "./type";

export const Confirm: FC<Props> = ({
  questions,
  clientNameValue,
  shopNameValue,
  brandNameValue,
  workTitleValue,
  businessTypeValue,
  occupationValue,
  employmentValue,
  employmentStatusValue,
  continuedServiceYearsValue,
  checkboxValues,
  radioValue,
  textAreaValue,
  satisfactionRateValue,
  termsPath,
  guidelinePath,
  handleClickPrevStepButton,
  handleClickSubmitButton,
  salaryArray,
  bonusArray,
  hasOtherOption = false,
  isJobEntryClient,
}) => {
  return (
    <div className={styles.container}>
      <Table>
        <Tr>
          <Th width="185px">基本情報</Th>
          <Td>
            <dl className={styles.list}>
              <dt className={styles.listTitle}>投稿先</dt>
              <dd>企業名：{clientNameValue}</dd>
              {shopNameValue && <dd>店舗名：{shopNameValue}</dd>}
              {brandNameValue && <dd>ブランド名：{brandNameValue}</dd>}
              {workTitleValue && <dd>{workTitleValue}</dd>}
            </dl>
          </Td>
        </Tr>
        <Tr>
          <Th width="185px">就業情報</Th>
          <Td>
            <dl className={styles.list}>
              <dt className={styles.listTitle}>就業状況</dt>
              <dd>
                {questions.employmentQuestions.employmentStatus && (
                  <>
                    {questions.employmentQuestions.employmentStatus
                      .filter(
                        (employmentState) => employmentState.optionId === employmentStatusValue,
                      )
                      .map((employment) => (
                        <p key={employment.optionText}>{employment.optionText}</p>
                      ))}
                  </>
                )}
              </dd>
              <dt className={styles.listTitle}>この職場での勤続年数</dt>
              <dd>
                {questions.employmentQuestions.continuedServiceYearsSelectOptions && (
                  <>
                    {questions.employmentQuestions.continuedServiceYearsSelectOptions
                      .filter(
                        (continuedServiceYearsOption) =>
                          continuedServiceYearsOption.value === continuedServiceYearsValue,
                      )
                      .map((continuedServiceYears) => (
                        <p key={continuedServiceYears.label}>{continuedServiceYears.label}</p>
                      ))}
                  </>
                )}
              </dd>
              <dt className={styles.listTitle}>職種</dt>
              <dd>
                {questions.employmentQuestions.businessTypesSelectOptions && (
                  <>
                    {questions.employmentQuestions.businessTypesSelectOptions
                      .filter(
                        (businessTypeOption) => businessTypeOption.value === businessTypeValue,
                      )
                      .map((businessType) => (
                        <p key={businessType.label}>{businessType.label}</p>
                      ))}
                  </>
                )}
              </dd>
              <dt className={styles.listTitle}>{MODEL_ATTRIBUTE_NAME.workContent.occupation}</dt>
              <dd>
                {questions.employmentQuestions.occupationsSelectOptions && (
                  <>
                    {questions.employmentQuestions.occupationsSelectOptions
                      .filter((occupationOption) => occupationOption.value === occupationValue)
                      .map((occupation) => (
                        <p key={occupation.label}>{occupation.label}</p>
                      ))}
                  </>
                )}
              </dd>
              <dt className={styles.listTitle}>雇用形態</dt>
              <dd>
                {questions.employmentQuestions.employmentsSelectOptions && (
                  <>
                    {questions.employmentQuestions.employmentsSelectOptions
                      .filter((employmentOption) => employmentOption.value === employmentValue)
                      .map((employment) => (
                        <p key={employment.label}>{employment.label}</p>
                      ))}
                  </>
                )}
              </dd>
            </dl>
          </Td>
        </Tr>
        <Tr>
          <Th width="185px">あなたの職場の口コミ【待遇面】</Th>
          <Td>
            <dl className={styles.list}>
              <dt className={styles.listTitle}>あなたの職場の良いところを教えてください</dt>
              <dd>
                <ul className={styles.dotList}>
                  {questions.treatmentAndCultureQuestions.treatmentQuestions && (
                    <>
                      {questions.treatmentAndCultureQuestions.treatmentQuestions
                        .filter((item) => checkboxValues.treatments.includes(item.optionId))
                        .map((treatmentsQuestion) => (
                          <li key={treatmentsQuestion.optionText} className={styles.dotListItem}>
                            {treatmentsQuestion.optionText}
                          </li>
                        ))}
                    </>
                  )}
                </ul>
              </dd>
            </dl>
          </Td>
        </Tr>
        <Tr>
          <Th width="185px">あなたの職場の口コミ【社風面】</Th>
          <Td>
            <dl className={styles.list}>
              <dt className={styles.listTitle}>あなたの職場の良いところを教えてください</dt>
              <dd>
                <ul className={styles.dotList}>
                  {questions.treatmentAndCultureQuestions.cultureQuestions && (
                    <>
                      {questions.treatmentAndCultureQuestions.cultureQuestions
                        .filter((item) => checkboxValues.cultures.includes(item.optionId))
                        .map((culturesQuestion) => (
                          <li key={culturesQuestion.optionText} className={styles.dotListItem}>
                            {culturesQuestion.optionText}
                          </li>
                        ))}
                    </>
                  )}
                </ul>
              </dd>
            </dl>
          </Td>
        </Tr>
        <Tr>
          <Th width="185px">あなたの職場の口コミ【特徴】</Th>
          <Td>
            <dl className={styles.list}>
              <dt className={styles.listTitle}>あなたの職場の雰囲気は？</dt>
              <dd>
                {questions.workplaceReviewFeatureQuestions.atmosphere
                  .filter((item) => item.optionId === radioValue.atmosphere)
                  .map((atmosphereQuestion) => (
                    <p key={atmosphereQuestion.optionText}>{atmosphereQuestion.optionText}</p>
                  ))}
              </dd>
              <dt className={styles.listTitle}>あなたの職場の給与体系は？</dt>
              <dd>
                {questions.workplaceReviewFeatureQuestions.salaryStandard
                  .filter((item) => item.optionId === radioValue.salaryStandard)
                  .map((salaryStandardQuestion) => (
                    <p key={salaryStandardQuestion.optionText}>
                      {salaryStandardQuestion.optionText}
                    </p>
                  ))}
              </dd>
              <dt className={styles.listTitle}>独り立ち・デビューまでは？</dt>
              <dd>
                {questions.workplaceReviewFeatureQuestions.debut
                  .filter((item) => item.optionId === radioValue.debut)
                  .map((debutQuestion) => (
                    <p key={debutQuestion.optionText}>{debutQuestion.optionText}</p>
                  ))}
              </dd>
              <dt className={styles.listTitle}>教育方針は？</dt>
              <dd>
                {questions.workplaceReviewFeatureQuestions.education
                  .filter((item) => item.optionId === radioValue.education)
                  .map((educationQuestion) => (
                    <p key={educationQuestion.optionText}>{educationQuestion.optionText}</p>
                  ))}
              </dd>
              <dt className={styles.listTitle}>勤務時間は？</dt>
              <dd>
                {questions.workplaceReviewFeatureQuestions.workTime
                  .filter((item) => item.optionId === radioValue.workTime)
                  .map((workTimeQuestion) => (
                    <p key={workTimeQuestion.optionText}>{workTimeQuestion.optionText}</p>
                  ))}
              </dd>
              <dt className={styles.listTitle}>前職から比べた給与の変化</dt>
              <dd>
                {questions.workplaceReviewFeatureQuestions.salaryChange
                  .filter((item) => item.optionId === radioValue.salaryChange)
                  .map((salaryChangeQuestion) => (
                    <p key={salaryChangeQuestion.optionText}>{salaryChangeQuestion.optionText}</p>
                  ))}
              </dd>
            </dl>
          </Td>
        </Tr>
        {employmentValue !== EMPLOYMENT.OTHER_ID && (
          <Tr>
            <Th width="185px">給与データ</Th>
            <Td>
              <ConfirmSalariesDataContents salaryArray={salaryArray} bonusArray={bonusArray} />
            </Td>
          </Tr>
        )}
        {textAreaValue.workplaceMatching && (
          <Tr>
            <Th width="185px">
              {questions.workplaceReviewThoughtQuestions.workplaceMatching.questionText}
            </Th>
            <Td>
              <MultiLineText text={textAreaValue.workplaceMatching} />
            </Td>
          </Tr>
        )}
        {textAreaValue.interestReward && (
          <Tr>
            <Th width="185px">
              {questions.workplaceReviewThoughtQuestions.interestReward.questionText}
            </Th>
            <Td>
              <MultiLineText text={textAreaValue.interestReward} />
            </Td>
          </Tr>
        )}
        {textAreaValue.challenge && (
          <Tr>
            <Th width="185px">
              {questions.workplaceReviewThoughtQuestions.challenge.questionText}
            </Th>
            <Td>
              <MultiLineText text={textAreaValue.challenge} />
            </Td>
          </Tr>
        )}
        {textAreaValue.expectation && (
          <Tr>
            <Th width="185px">
              {questions.workplaceReviewThoughtQuestions.expectation.questionText}
            </Th>
            <Td>
              <MultiLineText text={textAreaValue.expectation} />
            </Td>
          </Tr>
        )}
        {/* 応募の決め手 - リジョブ経由の求人応募の場合のみ表示 */}
        {isJobEntryClient && checkboxValues.decidingFactors?.length > 0 && (
          <Tr>
            <Th width="185px">応募の決め手</Th>
            <Td>
              <dl className={styles.list}>
                <dt className={styles.listTitle}>
                  求人のどんな所を見て、応募しようと思いましたか？
                </dt>
                <dd>
                  <ul className={styles.dotList}>
                    {questions.decidingFactorsQuestions &&
                      Array.isArray(questions.decidingFactorsQuestions) && (
                        <>
                          {questions.decidingFactorsQuestions
                            .filter((item: { optionId: number; optionText: string }) =>
                              checkboxValues.decidingFactors?.includes(item.optionId),
                            )
                            .map(
                              (decidingFactorsQuestion: {
                                optionId: number;
                                optionText: string;
                              }) => (
                                <li
                                  key={decidingFactorsQuestion.optionText}
                                  className={styles.dotListItem}
                                >
                                  {decidingFactorsQuestion.optionText}
                                </li>
                              ),
                            )}
                        </>
                      )}
                  </ul>
                </dd>
              </dl>
            </Td>
          </Tr>
        )}
        {(satisfactionRateValue ||
          checkboxValues.chooseRejobReasons?.length > 0 ||
          (textAreaValue.chooseRejobReasonDetail && hasOtherOption) ||
          textAreaValue.thoughts) && (
          <Tr>
            <Th width="185px">リジョブに対する満足度</Th>
            <Td>
              <dl className={styles.list}>
                {satisfactionRateValue && (
                  <>
                    <dt className={styles.listTitle}>満足度を5段階で評価してください</dt>
                    <dd>
                      {questions.satisfactionAndThoughtsQuestions.rate?.options &&
                        Array.isArray(questions.satisfactionAndThoughtsQuestions.rate.options) && (
                          <>
                            {questions.satisfactionAndThoughtsQuestions.rate.options
                              .filter(
                                (option: { value: unknown; label: string }) =>
                                  option.value === satisfactionRateValue,
                              )
                              .map((option: { value: unknown; label: string }) => (
                                <p key={String(option.value)}>{option.label}</p>
                              ))}
                          </>
                        )}
                    </dd>
                  </>
                )}
                {checkboxValues.chooseRejobReasons?.length > 0 && (
                  <>
                    <dt className={styles.listTitle}>リジョブを利用した理由</dt>
                    <dd>
                      <ul className={styles.dotList}>
                        {questions.satisfactionAndThoughtsQuestions.reason &&
                          Array.isArray(questions.satisfactionAndThoughtsQuestions.reason) && (
                            <>
                              {questions.satisfactionAndThoughtsQuestions.reason
                                .filter((item: { optionId: number; optionText: string }) =>
                                  checkboxValues.chooseRejobReasons?.includes(item.optionId),
                                )
                                .map((reasonQuestion: { optionId: number; optionText: string }) => (
                                  <li
                                    key={reasonQuestion.optionText}
                                    className={styles.dotListItem}
                                  >
                                    {reasonQuestion.optionText}
                                  </li>
                                ))}
                            </>
                          )}
                      </ul>
                    </dd>
                  </>
                )}
                {textAreaValue.chooseRejobReasonDetail && hasOtherOption && (
                  <>
                    <dt className={styles.listTitle}>その他の理由</dt>
                    <dd>
                      <MultiLineText text={textAreaValue.chooseRejobReasonDetail} />
                    </dd>
                  </>
                )}
                {textAreaValue.thoughts && (
                  <>
                    <dt className={styles.listTitle}>感想</dt>
                    <dd>
                      <MultiLineText text={textAreaValue.thoughts} />
                    </dd>
                  </>
                )}
              </dl>
            </Td>
          </Tr>
        )}
      </Table>
      <div className={styles.panelOuter}>
        <RequestConfirmationPanel termsUrl={termsPath} guidelineUrl={guidelinePath} />
      </div>
      <div className={styles.buttonContainer}>
        <div className={styles.halfButtonOuter}>
          <Button theme="tertiary" onClick={handleClickPrevStepButton}>
            戻る
          </Button>
        </div>
        <div className={styles.halfButtonOuter}>
          <Button theme="primary" onClick={handleClickSubmitButton}>
            投稿する
          </Button>
        </div>
      </div>
    </div>
  );
};
